import React from 'react';
import { useResizeDetector } from 'react-resize-detector';
import { styled } from 'styled-components';
import { InView } from 'react-intersection-observer';

import Loading from 'components/Loading/Loading';
import { useSettlementRequestListHooks } from './hooks/useSettlementRequestListHooks';
import RequestListHeader from './components/RequestListHeader';
import RequestDescription from './components/RequestDescription';
import InvoiceList from './components/InvoiceList';
import Column from 'components/common/Column';
import Text from 'components/common/Text';
import Divider from 'components/Divider';
import InvoiceTableList from './components/InvoiceTableList';
import SettlementDetailModal from './components/SettlementDetailModal';
import SearchFilterContainer from './components/SearchFilterContainer';
import EmptySettlement from 'containers/Settlement/components/EmptySettlement';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;

  width: 100%;
`;

const Container = styled.div<{ $isMobile: boolean }>`
  display: flex;
  flex-direction: column;
  padding: ${props => (props.$isMobile ? '0 20px' : '0 40px 40px')};
  margin: 0 auto;

  width: 100%;
  max-width: ${props => (props.$isMobile ? '100%' : '1200px')};
  min-height: 100vh;

  border-left: 1px solid #dbdcdf;
  border-right: 1px solid #dbdcdf;

  background-color: #fff;

  @media (max-width: 768px) {
    border-left: none;
    border-right: none;
  }
`;

const SettlementRequestListPage = () => {
  const {
    invoices,
    invoiceFilter,
    isLoading,
    settlements,
    selectedInvoice,
    isOpenDetailModal,
    isOpenSearchModal,
    termType,
    statusIn,
    isAcceptFilter,
    customDate,
    setIsOpenSearchModal,
    handleOpenDetailModal,
    handleCloseDetailModal,
    handleLoadMoreInovice,
    handleResetInvoiceFilter,
    handleSetTermType,
    handleSetInvoiceState,
    handleSetProgressingState,
    handleAcceptFilter,
    handleOnCloseSearchModal,
    handleSetCustomDate,
  } = useSettlementRequestListHooks();

  const { ref, width } = useResizeDetector();

  const isMobile = width ? width < 768 : window.innerWidth < 768;

  const renderLoadMore = (): JSX.Element => {
    return (
      <InView
        onChange={(inView: boolean) => {
          if (inView && invoices.pageInfo.hasNextPage) {
            handleLoadMoreInovice();
          }
        }}
      />
    );
  };

  const renderInvoiceList = (): JSX.Element => {
    if (isMobile) {
      return (
        <Column gap="24px">
          {invoices.edges.map((invoiceItem, invoiceIdx) => {
            return (
              <React.Fragment key={invoiceIdx}>
                <InvoiceList
                  invoiceItem={invoiceItem}
                  handleOpenDetailModal={handleOpenDetailModal}
                />
                {isMobile && invoices.edges.length !== invoiceIdx + 1 && (
                  <Divider />
                )}
              </React.Fragment>
            );
          })}
          {renderLoadMore()}
        </Column>
      );
    }

    return (
      <Column>
        <InvoiceTableList
          invoices={invoices}
          handleOpenDetailModal={handleOpenDetailModal}
        />
        {renderLoadMore()}
      </Column>
    );
  };

  if (isLoading) {
    return <Loading />;
  }

  return (
    <Wrapper ref={ref}>
      <Container $isMobile={isMobile}>
        <RequestListHeader
          isMobile={isMobile}
          invoiceFilter={invoiceFilter}
          isAcceptFilter={isAcceptFilter}
          handleOpenSearchModal={() => setIsOpenSearchModal(true)}
        />
        {invoices && (
          <Column gap="24px" align="flex-start">
            <RequestDescription />
            <Text color="#1b1c1e" size="20px">
              총 건수: {invoices.totalCount}
            </Text>
            {invoices.totalCount === 0 ? (
              <EmptySettlement textContent="조회되는 내역이 없어요" />
            ) : (
              renderInvoiceList()
            )}
          </Column>
        )}
      </Container>
      {isOpenDetailModal && (
        <SettlementDetailModal
          isMobile={isMobile}
          settlements={settlements}
          selectedInvoice={selectedInvoice}
          handleCloseDetailModal={handleCloseDetailModal}
        />
      )}
      {isOpenSearchModal && (
        <SearchFilterContainer
          isMobile={isMobile}
          termType={termType}
          statusIn={statusIn}
          customDate={customDate}
          handleResetInvoiceFilter={handleResetInvoiceFilter}
          handleSetTermType={handleSetTermType}
          handleSetInvoiceState={handleSetInvoiceState}
          handleSetProgressingState={handleSetProgressingState}
          onAcceptFilter={handleAcceptFilter}
          setIsOpenSearchModal={setIsOpenSearchModal}
          onClose={handleOnCloseSearchModal}
          handleSetCustomDate={handleSetCustomDate}
        />
      )}
    </Wrapper>
  );
};

export default SettlementRequestListPage;
